import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonRef } from '@central/contracts';
import { Button } from '@/components/Button';
import { useAuthStore } from '@/features/auth/auth.store';
import { mensagemDeErro } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import { buscarContatos, buscarServicosParaOrg, chavesDeApoio } from '@/features/tickets/lookups.api';
import {
  apenasAtivos,
  listaDeImpactos,
  listaDeUrgencias,
  listaDeTipos,
  subcategoriasDaCategoria,
  useOpcoesDeFormulario,
} from '@/features/tickets/opcoes';
import { SeletorDePessoa } from '@/features/tickets/pessoas';
import { chaves, criarChamado, criarSubchamado } from '@/features/tickets/tickets.api';
import {
  type Chamado,
  type Nivel,
  type TipoDeChamado,
} from '@/features/tickets/types';

/**
 * Abertura de chamado — e de subchamado, quando vem com `pai`.
 *
 * São o mesmo formulário porque são o mesmo ato: o que muda é o endereço e o
 * fato de o filho herdar organização, solicitante e classificação do pai.
 * Duplicar a tela para trocar uma rota faria as duas divergirem na primeira
 * mudança de campo.
 */


export interface NewTicketDialogProps {
  aberto: boolean;
  aoFechar: () => void;
  /** Presente = abre um subchamado deste pai. */
  pai?: { id: string; number: string };
  aoCriar?: (chamado: Chamado) => void;
}

export function NewTicketDialog({ aberto, aoFechar, pai, aoCriar }: NewTicketDialogProps) {
  const usuario = useAuthStore((estado) => estado.user);
  const clienteDeConsulta = useQueryClient();
  const opcoes = useOpcoesDeFormulario();

  const [tipo, setTipo] = useState<TipoDeChamado>(pai ? 'TASK' : 'INCIDENT');
  const [assunto, setAssunto] = useState('');
  const [descricao, setDescricao] = useState('');
  const [organizacaoId, setOrganizacaoId] = useState('');
  const [servicoId, setServicoId] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subcategoriaId, setSubcategoriaId] = useState('');
  const [emNomeDeOutro, setEmNomeDeOutro] = useState(false);
  const [solicitante, setSolicitante] = useState<PersonRef | null>(null);
  const [impacto, setImpacto] = useState<Nivel>('MEDIUM');
  const [urgencia, setUrgencia] = useState<Nivel>('MEDIUM');
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const solicitanteId = emNomeDeOutro ? (solicitante?.id ?? '') : (usuario?.id ?? '');

  /**
   * Só as subcategorias da categoria escolhida.
   *
   * O par "Rede" + "Impressora" é recusado pelo banco por chave estrangeira
   * composta. A tela filtra para que o operador nem chegue a montá-lo — o erro
   * do servidor é a rede de segurança, não a interface do usuário.
   */
  const subcategorias = apenasAtivos(subcategoriasDaCategoria(opcoes.data, categoriaId));

  const criar = useMutation<Chamado, Error>({
    mutationFn: async () => {
      const descricaoHtml = descricao.trim() ? textoParaHtml(descricao) : undefined;
      const classificacao = {
        ...(categoriaId ? { categoryId: categoriaId } : {}),
        ...(categoriaId && subcategoriaId ? { subcategoryId: subcategoriaId } : {}),
      };
      if (pai) {
        return criarSubchamado(pai.id, {
          subject: assunto.trim(),
          type: tipo,
          ...(descricaoHtml ? { descriptionHtml: descricaoHtml } : {}),
          ...classificacao,
          impact: impacto,
          urgency: urgencia,
        });
      }
      return criarChamado({
        type: tipo,
        subject: assunto.trim(),
        ...(descricaoHtml ? { descriptionHtml: descricaoHtml } : {}),
        requesterId: solicitanteId,
        organizationId: organizacaoId,
        ...(servicoId ? { serviceId: servicoId } : {}),
        ...classificacao,
        impact: impacto,
        urgency: urgencia,
      });
    },
    onSuccess: (chamado) => {
      // Invalida a raiz inteira: a criação mexe na lista, e a do filho mexe
      // também na árvore e no progresso do pai — enumerar cada chave afetada
      // seria uma lista para esquecer de atualizar depois.
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.raiz });
      limpar();
      aoCriar?.(chamado);
      aoFechar();
    },
  });

  const servicosDaOrg = useQuery({
    queryKey: chavesDeApoio.servicosParaOrg(organizacaoId),
    queryFn: () => buscarServicosParaOrg(organizacaoId),
    enabled: Boolean(organizacaoId) && !pai,
  });

  const servicoSelecionado = servicosDaOrg.data?.find((s) => s.id === servicoId) ?? null;

  const buscarContatosDaOrg = useCallback(
    (termo: string) => buscarContatos(termo, organizacaoId || undefined),
    [organizacaoId],
  );

  function limpar() {
    setAssunto('');
    setDescricao('');
    setServicoId('');
    setCategoriaId('');
    setSubcategoriaId('');
    setImpacto('MEDIUM');
    setUrgencia('MEDIUM');
    setSolicitante(null);
    setEmNomeDeOutro(false);
    setErroLocal(null);
  }

  /**
   * Trocar a categoria descarta a subcategoria.
   *
   * Mantê-la seria manter uma escolha que já não é oferecida — e que o servidor
   * recusaria no envio, num campo que a tela ainda estaria exibindo como válido.
   */
  function escolherCategoria(id: string) {
    setCategoriaId(id);
    setSubcategoriaId('');
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErroLocal(null);

    const assuntoLimpo = assunto.trim();
    if (assuntoLimpo.length < 3) {
      setErroLocal('O assunto precisa de pelo menos 3 caracteres.');
      return;
    }
    if (!pai) {
      if (!organizacaoId) {
        setErroLocal('Escolha a organização do chamado.');
        return;
      }
      if (emNomeDeOutro && !solicitante) {
        setErroLocal('Escolha o solicitante pelo nome.');
        return;
      }
      // Exigida na abertura de chamado raiz, não no banco: a coluna aceita nulo
      // porque chamado importado e chamado nascido de e-mail chegam sem
      // classificação e precisam ser gravados assim mesmo. Quem abre pela tela
      // tem a lista na frente, e a categoria decide fila e política de SLA.
      if (!categoriaId) {
        setErroLocal('Escolha a categoria do chamado.');
        return;
      }
    }
    criar.mutate();
  }

  const erro = erroLocal ?? mensagemDeErro(criar.error);

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={pai ? `Novo subchamado de ${pai.number}` : 'Abrir chamado'}
      descricao={
        pai
          ? 'Organização, solicitante e classificação são herdadas do chamado pai.'
          : 'O identificador é gerado pela Central no momento da abertura.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="tipo" rotulo="Tipo">
            <select
              id="tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as TipoDeChamado)}
              className={CLASSE_DE_CAMPO}
            >
              {listaDeTipos(opcoes.data).filter((t) => t.active).map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
          </Campo>

          {pai ? null : (
            <Campo id="organizacao" rotulo="Organização">
              <select
                id="organizacao"
                value={organizacaoId}
                disabled={opcoes.isPending || opcoes.isError}
                onChange={(evento) => { setOrganizacaoId(evento.target.value); setServicoId(''); }}
                className={CLASSE_DE_CAMPO}
              >
                <option value="">
                  {opcoes.isPending ? 'Carregando…' : 'Selecione a organização'}
                </option>
                {/* Só as ativas: a lista vem inteira da API para resolver o nome
                    de chamados antigos, mas oferecer uma organização desativada
                    para um chamado novo é dado errado entrando pela porta da
                    frente. */}
                {apenasAtivos(opcoes.data?.organizations ?? []).map((organizacao) => (
                  <option key={organizacao.id} value={organizacao.id}>
                    {organizacao.name}
                  </option>
                ))}
              </select>
              {opcoes.isError ? (
                <p className="text-xs text-destructive">
                  {mensagemDeErro(opcoes.error) ?? 'Não foi possível carregar as organizações.'}
                </p>
              ) : null}
            </Campo>
          )}
        </div>

        {!pai && organizacaoId ? (
          <Campo id="servico" rotulo="Serviço" opcional>
            <select
              id="servico"
              value={servicoId}
              disabled={servicosDaOrg.isPending}
              onChange={(evento) => setServicoId(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">
                {servicosDaOrg.isPending
                  ? 'Carregando serviços…'
                  : servicosDaOrg.data?.length === 0
                    ? 'Nenhum serviço contratado'
                    : 'Selecione o serviço (opcional)'}
              </option>
              {(servicosDaOrg.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {servicoSelecionado ? (
              <p className="text-xs text-muted-foreground">
                {servicoSelecionado.slaPolicyName
                  ? `SLA: ${servicoSelecionado.slaPolicyName}`
                  : 'Sem política de SLA contratual — o prazo virá da equipe responsável.'}
              </p>
            ) : null}
          </Campo>
        ) : null}

        <Campo id="assunto" rotulo="Assunto">
          <input
            id="assunto"
            required
            maxLength={300}
            value={assunto}
            onChange={(evento) => setAssunto(evento.target.value)}
            placeholder="Resuma o que aconteceu"
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo id="descricao" rotulo="Descrição" opcional>
          <textarea
            id="descricao"
            rows={5}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            placeholder="O que foi observado, desde quando, o que já foi tentado."
            className={`${CLASSE_DE_CAMPO} resize-y`}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="categoria" rotulo="Categoria" opcional={Boolean(pai)}>
            <select
              id="categoria"
              value={categoriaId}
              disabled={opcoes.isPending || opcoes.isError}
              onChange={(evento) => escolherCategoria(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">
                {opcoes.isPending
                  ? 'Carregando…'
                  : pai
                    ? 'Herdar do chamado pai'
                    : 'Selecione a categoria'}
              </option>
              {apenasAtivos(opcoes.data?.categories ?? []).map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.name}
                </option>
              ))}
            </select>
          </Campo>

          <Campo id="subcategoria" rotulo="Subcategoria" opcional>
            <select
              id="subcategoria"
              value={subcategoriaId}
              // Sem categoria não há o que oferecer: a subcategoria pertence a
              // uma categoria, e o banco recusa o par que não bate.
              disabled={!categoriaId || subcategorias.length === 0}
              onChange={(evento) => setSubcategoriaId(evento.target.value)}
              className={`${CLASSE_DE_CAMPO} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <option value="">
                {categoriaId ? 'Sem subcategoria' : 'Escolha a categoria primeiro'}
              </option>
              {subcategorias.map((subcategoria) => (
                <option key={subcategoria.id} value={subcategoria.id}>
                  {subcategoria.name}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="impacto" rotulo="Impacto">
            <select
              id="impacto"
              value={impacto}
              onChange={(evento) => setImpacto(evento.target.value as Nivel)}
              className={CLASSE_DE_CAMPO}
            >
              {listaDeImpactos(opcoes.data).filter((i) => i.active).map((i) => (
                <option key={i.level} value={i.level}>
                  {i.label}
                </option>
              ))}
            </select>
          </Campo>

          <Campo id="urgencia" rotulo="Urgência">
            <select
              id="urgencia"
              value={urgencia}
              onChange={(evento) => setUrgencia(evento.target.value as Nivel)}
              className={CLASSE_DE_CAMPO}
            >
              {listaDeUrgencias(opcoes.data).filter((u) => u.active).map((u) => (
                <option key={u.level} value={u.level}>
                  {u.label}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <p className="text-xs text-muted-foreground">
          A prioridade sai da matriz de impacto × urgência no servidor — não é escolhida aqui.
        </p>

        {pai ? null : (
          <div className="space-y-2 rounded-md border border-border bg-secondary/40 px-3 py-2.5">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={emNomeDeOutro}
                onChange={(evento) => {
                  setEmNomeDeOutro(evento.target.checked);
                  setSolicitante(null);
                }}
                className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
              />
              Abrir em nome de outra pessoa
            </label>

            {emNomeDeOutro ? (
              <SeletorDePessoa
                rotulo="Solicitante"
                escolhida={solicitante}
                aoEscolher={setSolicitante}
                placeholder="Nome ou e-mail do contato"
                buscadorFn={buscarContatosDaOrg}
                chaveDeBusca={chavesDeApoio.buscaDeContatos('', organizacaoId || undefined)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Solicitante: <strong className="font-medium text-foreground">{usuario?.name}</strong>
              </p>
            )}
          </div>
        )}

        {erro ? (
          <p role="alert" className="text-sm text-destructive">
            {erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={criar.isPending}>
            {pai ? 'Criar subchamado' : 'Abrir chamado'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const CLASSE_DE_CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow';

function Campo({
  id,
  rotulo,
  opcional = false,
  children,
}: {
  id: string;
  rotulo: string;
  opcional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-baseline gap-1.5 text-sm font-medium">
        {rotulo}
        {opcional ? <span className="text-xs font-normal text-muted-foreground">opcional</span> : null}
      </label>
      {children}
    </div>
  );
}

/**
 * Converte o texto digitado em HTML escapado.
 *
 * A descrição do chamado **não** passa pelo higienizador do servidor — só
 * comentário e e-mail passam. Mandar o texto cru num campo chamado
 * `descriptionHtml` gravaria marcação de quem digitasse `<script>`, então o
 * escape acontece antes de sair daqui.
 */
function textoParaHtml(texto: string): string {
  return texto
    .split(/\n{2,}/)
    .map((paragrafo) => `<p>${escapar(paragrafo).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
